import { useNavigate } from 'react-router-dom';
import TimeFilters from '../../../../components/time-filters';
import './reports-time-page.scss';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../../../../services/api.service';
import { AppStore } from '../../../../store/store';
import { UserState } from '../../../../store/user.slice';
import StatisticSection from '../../../../components/statistics-section';
import StatisticsSectionBarChartType from '../../../../components/statistics-section.bar-chart-type';
import StatisticsSectionChartType from '../../../../components/statistics-section.chart-type';
import { colorsPalette } from '../../../../utils/colors-utils';
import { getColorByStatusType, getColorByCardBrandType, transformTransactionsToChartLineData, transformCardBrandToChartLineData, transformToChartLineData } from '../../../../utils/constants';
import StatisticsSectionLineChart from '../../../../components/statistics-section.line-chart';
import { StatisticLineChartData } from '../../../../types/statistics.types';

const ReportsTimePage: React.FC = () => {
    const navigate = useNavigate();

    const [startDate, setStartDate] = useState<string>();
    const [endDate, setEndDate] = useState<string>();

    const [transactionsStatus, setTransactionsStatus] = useState();
    const [statusLoader, setStatusLoader] = useState(false);
    const [transactionsChartStatus, setTransactionsChartStatus] = useState<StatisticLineChartData[]>();
    const [transactionsChartLoader, setTransactionsChartLoader] = useState(false);

    const [transactionsNumber, setTransactionsNumber] = useState();

    const [cardTypes, setCardTypes] = useState([]);
    const [cardsLoader, setCardsLoader] = useState(false);
    const [cardChartTypes, setCardChartTypes] = useState<StatisticLineChartData[]>();
    const [cardChartLoader, setCardChartLoader] = useState(false);

    const [processors, setProcessors] = useState();
    const [processorsLoader, setProcessorsLoader] = useState(false);
    const [processorsChart, setProcessorsChart] = useState<StatisticLineChartData[]>();
    const [processorsChartLoader, setProcessorsChartLoader] = useState(false);

    const [merchants, setMerchants] = useState();
    const [merchantsLoader, setMerchantsLoader] = useState(false);
    const [merchantsChart, setMerchantsChart] = useState<StatisticLineChartData[]>();
    const [merchantsChartLoader, setMerchantsChartLoader] = useState(false);


    const { user } = useSelector<AppStore, UserState>((state) => state.user)

    const isMerchant = (user?.roles?.includes("merchant") || user?.roles?.includes("bas_merchant_view")) ?? false;


    useEffect(() => {
        const merchantId = isMerchant ? user?.id : undefined;

        apiService.getTransactionsSummary({ startDate, endDate, merchantId })
            .then((response) => { setTransactionsNumber(response.data.transactionCount) })
            .catch((error) => console.error('Error fetching transactions number:', error));


        setStatusLoader(true)
        apiService.getTransactionsStatusStatistics({ startDate, endDate, merchantId })
            .then((response) => {
                const transformedData = response.data.map((item: any) => ({
                    color: getColorByStatusType(item.name),
                    name: item.name,
                    quantitativePercent: item.quantityAndQuality?.quantitativePercent || 0,
                    quantitativeValue: item.quantityAndQuality?.quantitativeValue || 0,
                    qualitativePercent: item.quantityAndQuality?.qualitativePercent || 0,
                    qualitativeValue: item.quantityAndQuality?.qualitativeValue || 0,
                }));
                setTransactionsStatus(transformedData);
            })
            .catch((error) => console.error('Error fetching transactions status:', error))
            .finally(() => setStatusLoader(false))


        setTransactionsChartLoader(true)
        apiService.getChartTransactionsData()
            .then((response) => {
                const transactionChartData: StatisticLineChartData[] = transformTransactionsToChartLineData(response.data);
                setTransactionsChartStatus(transactionChartData);
            })
            .catch((error) => console.error('Error fetching transactions chart status:', error))
            .finally(() => setTransactionsChartLoader(false))

        setCardsLoader(true)
        apiService.getCardTypeStatistics({ startDate, endDate, merchantId })
            .then((response) => {
                const transformedData = response.data.map((item: any) => ({
                    color: getColorByCardBrandType(item.name),
                    name: item.name,
                    quantitativePercent: item.quantityAndQuality?.quantitativePercent || 0,
                    quantitativeValue: item.quantityAndQuality?.quantitativeValue || 0,
                    qualitativePercent: item.quantityAndQuality?.qualitativePercent || 0,
                    qualitativeValue: item.quantityAndQuality?.qualitativeValue || 0,
                }));
                setCardTypes(transformedData);
            })
            .catch((error) => console.error('Error fetching card types:', error))
            .finally(() => setCardsLoader(false))

        setCardChartLoader(true)
        apiService.getChartCardData()
            .then((response) => {
                const cardsChartData: StatisticLineChartData[] = transformCardBrandToChartLineData(response.data);
                setCardChartTypes(cardsChartData);
            })
            .catch((error) => console.error('Error fetching card chart data:', error))
            .finally(() => setCardChartLoader(false))

        setProcessorsLoader(true)
        apiService.getProcessorStatistics({ startDate, endDate, merchantId })
            .then((response) => {
                const transformedData = response.data.map((item: any, index: number) => {
                    return ({
                        color: colorsPalette[index],
                        name: item.name,
                        type: isMerchant ? null : item.type,
                        quantitativePercent: item.quantityAndQuality?.quantitativePercent || 0,
                        quantitativeValue: item.quantityAndQuality?.quantitativeValue || 0,
                        qualitativePercent: item.quantityAndQuality?.qualitativePercent || 0,
                        qualitativeValue: item.quantityAndQuality?.qualitativeValue || 0,
                    })
                });
                setProcessors(transformedData);
            })
            .catch((error) => console.error('Error fetching processors:', error))
            .finally(() => setProcessorsLoader(false))

        setProcessorsChartLoader(true)
        apiService.getChartProcessorData()
            .then((response) => {
                const processorChartData: StatisticLineChartData[] = transformToChartLineData(response.data);
                setProcessorsChart(processorChartData);
            })
            .catch((error) => console.error('Error fetching processor chart data:', error))
            .finally(() => setProcessorsChartLoader(false))

        if (!isMerchant && user) {
            setMerchantsLoader(true)
            apiService.getMerchantStatistics({ startDate, endDate })
                .then((response) => {
                    const transformedData = response.data.map((item: any, index: number) => ({
                        id: item.id,
                        color: colorsPalette[index],
                        name: item.name,
                        quantitativePercent: item.quantityAndQuality?.quantitativePercent || 0,
                        quantitativeValue: item.quantityAndQuality?.quantitativeValue || 0,
                        qualitativePercent: item.quantityAndQuality?.qualitativePercent || 0,
                        qualitativeValue: item.quantityAndQuality?.qualitativeValue || 0,
                    }));
                    setMerchants(transformedData);
                })
                .catch((error) => console.error('Error fetching merchants:', error))
                .finally(() => setMerchantsLoader(false))


            setMerchantsChartLoader(true)
            apiService.getChartMerchantData()
                .then((response) => {
                    const merchantsChartData: StatisticLineChartData[] = transformToChartLineData(response.data);
                    setMerchantsChart(merchantsChartData);
                })
                .catch((error) => console.error('Error fetching merchant chart data:', error))
                .finally(() => setMerchantsChartLoader(false))
        }
    }, [user?.id, startDate, endDate]);

    const handleMerchantNavigate = (id: string) => {
        navigate(`/app/merchants/merchant/${id}`);
    }


    const handleDataUpdate = (startDate?: string, endDate?: string) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }

    return (
        <div className='reports-time-page page-content'>
            <div className="page-header flex-row flex-gap-20 flex-align-start-center">
                <div className='page-title'>
                    Reports Time
                </div>
            </div>
            <div>
                <TimeFilters transactionsNo={transactionsNumber} handleFilters={handleDataUpdate} />
            </div>
            <StatisticSection title='Status Statistics'>
                <StatisticsSectionChartType data={transactionsStatus || []} isLoading={statusLoader} />
                <StatisticsSectionLineChart data={transactionsChartStatus || []} isLoading={transactionsChartLoader} />
            </StatisticSection>
            <StatisticSection title='Card Brand Statistics'>
                <StatisticsSectionChartType data={cardTypes || []} isLoading={cardsLoader} />
                <StatisticsSectionLineChart data={cardChartTypes || []} isLoading={cardChartLoader} />
            </StatisticSection>
            <StatisticSection title='Processors Statistics'>
                <StatisticsSectionBarChartType data={processors || []} isLoading={processorsLoader} />
                <StatisticsSectionLineChart data={processorsChart || []} isLoading={processorsChartLoader} />
            </StatisticSection>
            {!isMerchant &&
                <StatisticSection title='Merchants Statistics'>
                    <StatisticsSectionBarChartType data={merchants || []} onNavigate={handleMerchantNavigate} isLoading={merchantsLoader} />
                    <StatisticsSectionLineChart data={merchantsChart || []} isLoading={merchantsChartLoader} />
                </StatisticSection>
            }
        </div>
    )
}

export default ReportsTimePage